import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../../../shared/ui/Seo";
import { requestGuestCoupon } from "../api";
import "../styles/guest-coupon.css";

const STEPS = [
  {
    icon: "ph-device-mobile",
    title: "Enter your number",
    text: "Share the Sri Lankan mobile number you'd like the coupon sent to.",
  },
  {
    icon: "ph-chat-circle-text",
    title: "Get it by SMS",
    text: "Your coupon code is texted to you within moments.",
  },
  {
    icon: "ph-shopping-bag-open",
    title: "Apply at checkout",
    text: "Enter the code in your cart and the discount is applied.",
  },
];

// Accepts 7XXXXXXXX, 07XXXXXXXX, 947XXXXXXXX or +94 7XXXXXXXX and returns the
// 9-digit national number, or null when it isn't a valid Sri Lankan mobile.
const toNationalNumber = (value) => {
  let digits = value.replace(/\D/g, "");
  if (digits.startsWith("94") && digits.length === 11) digits = digits.slice(2);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return /^7\d{8}$/.test(digits) ? digits : null;
};

const formatInput = (value) => {
  const digits = value.replace(/\D/g, "");
  const local = digits.startsWith("0") ? digits.slice(0, 10) : digits.slice(0, 9);
  const offset = local.startsWith("0") ? 1 : 0;
  return [
    local.slice(0, 2 + offset),
    local.slice(2 + offset, 5 + offset),
    local.slice(5 + offset),
  ]
    .filter(Boolean)
    .join(" ");
};

const GuestCoupon = () => {
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | sent | claimed
  const [fieldError, setFieldError] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [serverMessage, setServerMessage] = useState("");
  const inputRef = useRef(null);

  const handleChange = (e) => {
    setPhone(formatInput(e.target.value));
    if (fieldError) setFieldError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const national = toNationalNumber(phone);
    if (!national) {
      setFieldError("Enter a valid mobile number, e.g. 77 123 4567.");
      inputRef.current?.focus();
      return;
    }

    setStatus("loading");
    setFieldError("");
    try {
      // 201 = sent; every failure (400 already sent / invalid, 429 rate limit, 502 SMS failed) is thrown
      const response = await requestGuestCoupon(`0${national}`);
      setMaskedPhone(response?.object?.phoneNumber || "");
      setStatus("sent");
    } catch (err) {
      const message = err.response?.data?.message;
      if (message && /already/i.test(message)) {
        setServerMessage(message);
        setStatus("claimed");
      } else {
        setFieldError(message || "We couldn't send your coupon right now. Please try again.");
        setStatus("idle");
      }
    }
  };

  const reset = () => {
    setPhone("");
    setMaskedPhone("");
    setServerMessage("");
    setStatus("idle");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const isLoading = status === "loading";
  const isSent = status === "sent";

  return (
    <section className="gc">
      <Seo
        title="Get Your Welcome Coupon"
        description="Enter your mobile number and receive an exclusive Bigmall coupon by SMS."
      />

      <div className="container container-lg">
        <div className="gc-card">
          {/* Visual panel */}
          <div className="gc-visual">
            <span className="gc-blob gc-blob--one" aria-hidden="true" />
            <span className="gc-blob gc-blob--two" aria-hidden="true" />

            <div className="gc-visual__content">
              <span className="gc-eyebrow">
                <i className="ph ph-sparkle" aria-hidden="true" /> Exclusive for guests
              </span>
              <h1 className="gc-title">
                A welcome gift, <br className="d-none d-sm-inline" />
                sent straight to your phone.
              </h1>
              <p className="gc-lead">
                No account needed. Tell us where to send it and we'll text you a
                Bigmall coupon to use on your next order.
              </p>

              <div className="gc-ticket-wrap" aria-hidden="true">
                <div className={`gc-ticket ${isSent ? "is-sent" : ""}`}>
                  <div className="gc-ticket__stub">
                    <span className="gc-ticket__icon">
                      <i className={`ph ${isSent ? "ph-check" : "ph-gift"}`} />
                    </span>
                  </div>
                  <div className="gc-ticket__body">
                    <span className="gc-ticket__label">Bigmall coupon</span>
                    <span className="gc-ticket__heading">Welcome gift</span>
                    <span className="gc-ticket__code">
                      {isSent ? "Sent via SMS" : "•••• •••• ••••"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form panel */}
          <div className="gc-panel" aria-live="polite">
            {(status === "idle" || isLoading) && (
              <div className="gc-state" key="form">
                <h2 className="gc-panel__title">Claim your coupon</h2>
                <p className="gc-panel__text">
                  Enter your mobile number below. One coupon per number.
                </p>

                <form className="gc-form" onSubmit={handleSubmit} noValidate>
                  <label htmlFor="gc-phone" className="gc-label">
                    Mobile number
                  </label>
                  <div
                    className={`gc-field ${fieldError ? "has-error" : ""} ${
                      isLoading ? "is-disabled" : ""
                    }`}
                  >
                    <span className="gc-field__prefix" aria-hidden="true">
                      <i className="ph ph-phone" />
                      +94
                    </span>
                    <input
                      ref={inputRef}
                      id="gc-phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="77 123 4567"
                      value={phone}
                      onChange={handleChange}
                      disabled={isLoading}
                      aria-invalid={!!fieldError}
                      aria-describedby={fieldError ? "gc-phone-error" : "gc-phone-hint"}
                    />
                  </div>
                  {fieldError ? (
                    <p id="gc-phone-error" className="gc-hint gc-hint--error" role="alert">
                      <i className="ph ph-warning-circle" aria-hidden="true" />
                      {fieldError}
                    </p>
                  ) : (
                    <p id="gc-phone-hint" className="gc-hint">
                      Sri Lankan mobile numbers only.
                    </p>
                  )}

                  <button type="submit" className="gc-btn" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <span className="gc-spinner" aria-hidden="true" />
                        Sending your coupon…
                      </>
                    ) : (
                      <>
                        Send my coupon
                        <i className="ph ph-arrow-right" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>

                <p className="gc-privacy">
                  <i className="ph ph-lock-simple" aria-hidden="true" />
                  We only use your number to deliver this coupon.
                </p>
              </div>
            )}

            {isSent && (
              <div className="gc-state gc-state--result" key="sent">
                <svg className="gc-check" viewBox="0 0 52 52" aria-hidden="true">
                  <circle className="gc-check__circle" cx="26" cy="26" r="24" />
                  <path className="gc-check__mark" d="M15 27l7 7 15-16" />
                </svg>
                <h2 className="gc-panel__title">Your coupon is on its way</h2>
                <p className="gc-panel__text">
                  We've texted it to <strong className="gc-masked">{maskedPhone}</strong>.
                  It usually arrives within a minute.
                </p>
                <div className="gc-actions">
                  <Link to="/" className="gc-btn">
                    Start shopping
                    <i className="ph ph-arrow-right" aria-hidden="true" />
                  </Link>
                  <button type="button" className="gc-btn gc-btn--ghost" onClick={reset}>
                    Use a different number
                  </button>
                </div>
              </div>
            )}

            {status === "claimed" && (
              <div className="gc-state gc-state--result" key="claimed">
                <span className="gc-badge-icon">
                  <i className="ph ph-info" aria-hidden="true" />
                </span>
                <h2 className="gc-panel__title">Already claimed</h2>
                <p className="gc-panel__text">
                  {serverMessage} Check your SMS inbox for the code we sent earlier.
                </p>
                <div className="gc-actions">
                  <Link to="/" className="gc-btn">
                    Continue shopping
                    <i className="ph ph-arrow-right" aria-hidden="true" />
                  </Link>
                  <button type="button" className="gc-btn gc-btn--ghost" onClick={reset}>
                    Try another number
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* How it works */}
        <div className="gc-steps">
          <h2 className="gc-steps__title">How it works</h2>
          <ol className="gc-steps__list">
            {STEPS.map((step, idx) => (
              <li className="gc-step" key={step.title}>
                <span className="gc-step__icon">
                  <i className={`ph ${step.icon}`} aria-hidden="true" />
                </span>
                <span className="gc-step__num">Step {idx + 1}</span>
                <h3 className="gc-step__title">{step.title}</h3>
                <p className="gc-step__text">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default GuestCoupon;
